import { useBookTocData } from '@/components/books/toc/book-toc-contexts';
import {
    getActivitySidebarHandle,
    type ActivitySidebarDeepLinkProps,
    type ActivitySidebarPanelProps,
} from '@/components/layout/activity-sidebar/types';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type { BookTocLoaderData } from '@/pages/book-toc.loader';
import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { Link, useLocation, useMatches, useRouteLoaderData, useSearchParams, type UIMatch } from 'react-router';

export function parseActivitySidebarDeepLink(
    searchParams: URLSearchParams,
    validActionIds: Set<string>,
): { actionId: string | null; sectionId: string | null } {
    const panel = searchParams.get('panel')?.trim() ?? '';
    const actionId = panel !== '' && validActionIds.has(panel) ? panel : null;
    const sectionRaw = searchParams.get('section')?.trim() ?? '';
    const sectionId = sectionRaw !== '' ? sectionRaw : null;
    return { actionId, sectionId };
}

export function buildActivitySidebarDeepLinkProps(
    activeActionId: string | null,
    deepLinkSectionId: string | null,
    sharePanel: {
        pickedSectionId: string | null;
        onPickedSectionIdChange: (sectionId: string) => void;
    },
): ActivitySidebarDeepLinkProps {
    if (activeActionId === 'share-section') {
        return {
            pickedSectionId: sharePanel.pickedSectionId,
            onPickedSectionIdChange: sharePanel.onPickedSectionIdChange,
            focusTargetBooksForSectionId: deepLinkSectionId ?? undefined,
        };
    }
    if (activeActionId === 'unplaced-sections' || activeActionId === 'trashed-sections') {
        return { highlightSectionId: deepLinkSectionId ?? undefined };
    }
    return {};
}

function ActivitySidebarPanelContent({
    Panel,
    deepLinkProps,
}: {
    Panel: ComponentType<ActivitySidebarPanelProps>;
    deepLinkProps: ActivitySidebarDeepLinkProps;
}) {
    const tocData = useBookTocData();

    return (
        <Panel
            book={tocData.book}
            chapters={tocData.optimisticChapters}
            unplacedSections={tocData.unplacedSections}
            deletedSections={tocData.deletedSections}
            sharedDocuments={tocData.sharedDocuments}
            {...deepLinkProps}
        />
    );
}

function ActionBadge({ count }: { count: number }) {
    return (
        <span className="bg-primary text-primary-foreground absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full text-[10px] font-semibold">
            {count > 9 ? '9+' : count}
        </span>
    );
}

function ActivitySidebarChromeActive({
    match,
    activitySidebar,
}: {
    match: UIMatch;
    activitySidebar: NonNullable<ReturnType<typeof getActivitySidebarHandle>>;
}) {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const tocData = useBookTocData();

    const [activeActionId, setActiveActionId] = useState<string | null>(null);
    const [deepLinkSectionId, setDeepLinkSectionId] = useState<string | null>(null);
    const [sharePickedSectionId, setSharePickedSectionId] = useState<string | null>(null);

    const visibleActions = useMemo(() => {
        if (!tocData.canManageToc) {
            return [];
        }
        return activitySidebar.actions.filter(action => action.id !== 'sync-book' || tocData.book.development !== false);
    }, [activitySidebar, tocData.book, tocData.canManageToc]);
    const validActionIds = useMemo(() => new Set(visibleActions.map(action => action.id)), [visibleActions]);

    useEffect(() => {
        const { actionId, sectionId } = parseActivitySidebarDeepLink(searchParams, validActionIds);
        if (!actionId) {
            return;
        }

        // Leaving as is, not using useEffect here would require larger changes unrelated to this
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setActiveActionId(actionId);
        setDeepLinkSectionId(sectionId);
        if (actionId === 'share-section') {
            setSharePickedSectionId(sectionId);
        }

        const next = new URLSearchParams(searchParams);
        next.delete('panel');
        next.delete('section');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, validActionIds]);

    const activeAction = visibleActions.find(action => action.id === activeActionId);
    const badgeContext = {
        unplacedCount: tocData.unplacedSections.length,
        deletedCount: tocData.deletedSections?.length ?? 0,
    };
    const deepLinkProps = buildActivitySidebarDeepLinkProps(activeActionId, deepLinkSectionId, {
        pickedSectionId: sharePickedSectionId,
        onPickedSectionIdChange: setSharePickedSectionId,
    });
    const bookIdParam = match.params.bookName;
    const bookId = bookIdParam ? decodeURIComponent(bookIdParam) : null;
    const links = tocData.canManageToc ? (activitySidebar.links ?? []) : [];

    if (visibleActions.length === 0 && links.length === 0) {
        return null;
    }

    function handleActionClick(actionId: string) {
        setDeepLinkSectionId(null);
        setActiveActionId(current => {
            if (current === actionId) {
                return null;
            }
            if (actionId === 'share-section') {
                setSharePickedSectionId(null);
            }
            return actionId;
        });
    }

    return (
        <div
            key={location.pathname}
            className="flex h-svh shrink-0 border-l"
        >
            {activeAction && (
                <aside className="flex w-72 flex-col border-r">
                    <div className="border-b p-3 font-semibold">{activeAction.label}</div>
                    <div className="min-h-0 flex-1 overflow-y-auto">
                        <ActivitySidebarPanelContent
                            Panel={activeAction.Panel}
                            deepLinkProps={deepLinkProps}
                        />
                    </div>
                </aside>
            )}
            <nav
                className="flex w-12 flex-col items-center gap-1 py-2"
                aria-label="Activity sidebar"
            >
                {visibleActions.map(action => {
                    const Icon = action.icon;
                    const isActive = activeActionId === action.id;
                    const badgeCount = action.getBadgeCount?.(badgeContext);
                    return (
                        <Button
                            key={action.id}
                            type="button"
                            variant="ghost"
                            size="icon"
                            title={action.label}
                            aria-label={action.label}
                            aria-pressed={isActive}
                            className={cn('relative', isActive && 'bg-accent')}
                            onClick={() => handleActionClick(action.id)}
                        >
                            <Icon className="size-5" />
                            {badgeCount != null && badgeCount > 0 ? <ActionBadge count={badgeCount} /> : null}
                        </Button>
                    );
                })}
                {bookId && links.length > 0 ? (
                    <>
                        <Separator className="my-1 w-6" />
                        {links.map(link => {
                            const Icon = link.icon;
                            return (
                                <Button
                                    key={link.id}
                                    variant="ghost"
                                    size="icon"
                                    title={link.label}
                                    aria-label={link.label}
                                    asChild
                                >
                                    <Link to={link.getTo({ bookId })}>
                                        <Icon className="size-5" />
                                    </Link>
                                </Button>
                            );
                        })}
                    </>
                ) : null}
            </nav>
        </div>
    );
}

export function ActivitySidebarChrome() {
    const matches = useMatches();
    const match = [...matches].reverse().find(entry => getActivitySidebarHandle(entry.handle));
    const activitySidebar = match ? getActivitySidebarHandle(match.handle) : null;
    const loaderData = useRouteLoaderData(match?.id ?? '') as BookTocLoaderData | undefined;

    if (!match || !activitySidebar || !loaderData) {
        return null;
    }

    return (
        <ActivitySidebarChromeActive
            match={match}
            activitySidebar={activitySidebar}
        />
    );
}
